import store from '@/store'
import { Action, config, getModule, Module, Mutation, VuexModule } from "vuex-module-decorators";
import Vue from "vue";
import Decisions from "@/store/modules/Decisions";
import Scoring from "@/store/modules/Scoring";
import { Viewpoint } from "@/api/model/Viewpoint";
import { VIEWPOINT_API } from "@/api/ViewpointApi";
import Modelling from "@/store/modules/Modelling";
import { ViewpointFactorResource } from "@/api/model/ViewpointFactorResource";
import _ from "lodash";
import { Factor, FactorGroup, FactorResource } from "@/api/model/FactorResource";
import { ViewpointChoiceFactor } from "@/api/model/ViewpointChoiceFactor";
import WorkspaceOptions from './WorkspaceOptions';
import { ViewpointList, ViewpointListItem } from '@/api/model/ResourceList';

config.rawError = true

const name = 'viewpoints';
if (module.hot) {
    if (store.hasModule(name)) {
        store.unregisterModule(name);
    }
}

const modelling = getModule(Modelling) as Modelling;
const scoring = getModule(Scoring);
const workspaceOptions = getModule(WorkspaceOptions);

@Module({dynamic: true, store: store, name: name, namespaced: true})
export default class Viewpoints extends VuexModule {

    viewpointFactorResources: { [viewpointId: string]: { [factorResourceId: string]: ViewpointFactorResource } } = {};
    viewpointChoiceFactorResource: { [viewpointId: string]: { [choiceId: string]: { [factorId: string]: ViewpointChoiceFactor } } } = {};

    // current selected viewpoint id
    activeViewpointId: string | null = null;
    // selected viewpoint ids are the visible viewpoints
    selectedViewpointIds: string[] = [];

    get decisionViewpoints(): Viewpoint[] | undefined {
        let decisionsModule = getModule(Decisions);
        if (decisionsModule.selectedDecision && decisionsModule.decisionViewpoints) {
            let workspaceModule = getModule(WorkspaceOptions);
            if (workspaceModule.activeViewpointLists && workspaceModule.activeViewpointLists.length > 0) {
                return decisionsModule.decisionViewpoints[decisionsModule.selectedDecision.decisionId].filter((vp: Viewpoint) => this.filteredViewpoints.includes(vp.viewpointId))

            }
            return decisionsModule.decisionViewpoints[decisionsModule.selectedDecision.decisionId]
        }
        return undefined;
    }

    get decisionViewpointsMap(): { [viewpointId: string]: Viewpoint } | undefined {
        if (this.decisionViewpoints) {
            return _.keyBy(this.decisionViewpoints, 'viewpointId')
        }
        return undefined;
    }

    get filteredViewpoints() {
        let workspaceModule = getModule(WorkspaceOptions);
        let allLists = getModule(Decisions).decisionLists
        let viewpointLists = allLists as { [listId: string]: ViewpointList };
        let activeViewpointLists = workspaceModule.activeViewpointLists;
        let filteredArr = [];
        if(activeViewpointLists.length > 0) {
            for (let id of Object.values(activeViewpointLists)) {
                // console.log(activeViewpointLists, activeViewpointLists[id])

                if (viewpointLists[id]) {
                    filteredArr = filteredArr.concat((viewpointLists[id].viewpoints.map((id: ViewpointListItem) => id.viewpointId as string)))
                } else {
                    continue;
                }

            }

            filteredArr = _.uniq(filteredArr);
            // console.log(filteredArr)
            return filteredArr;
        }
    }

    get selectedViewpoints() {
        if (this.decisionViewpointsMap) {
            let decisionMap = this.decisionViewpointsMap;
            return this.selectedViewpointIds
                .map(vId => decisionMap[vId])
                .filter(v => v)
        }
    }

    get selectedViewpointsMap() {
        if (this.selectedViewpoints) {
            return _.keyBy(this.selectedViewpoints, 'viewpointId')
        }
    }

    get contributingFactorResources() {
        let factorResources = modelling.publishedModelFactorResources;
        // console.log(factorResources)
        let validFactorResources = _.keyBy(Object.values(factorResources)
            .filter((f: FactorResource) => f.type === 'FACTOR')
            .filter((f: FactorResource) => {

                return this.selectedViewpointIds
                    .map(vId => (this.viewpointFactorResources[vId] || {})[f.id])
                    .filter(v => !v || v.numWeightingDots !== 0)
                    .length > 0
            }), 'id') as { [id: string]: Factor };

        let validFactorGroups = _.keyBy(Object.values(factorResources)
            .filter((f: FactorResource) => f.type === 'FACTOR_GROUP')
            .filter((fg: FactorResource) => Viewpoints.isGroupIncluded(fg as FactorGroup, factorResources, validFactorResources))
            , 'id')

        return _.merge(validFactorResources, validFactorGroups) as { [id: string]: FactorResource }
    }

    get activeViewpointContributingFactorResources() {
        let activeViewpointId = this.activeViewpointId || '';
        if (!activeViewpointId) return {};
        let factorResources = modelling.publishedModelFactorResources;
        let validFactorResources = _.keyBy(Object.values(factorResources)
            .filter((f: FactorResource) => f.type === 'FACTOR')
            .filter((f: FactorResource) => {
                let vf = (this.viewpointFactorResources[activeViewpointId] || {})[f.id]
                return !vf || vf.numWeightingDots !== 0
            }), 'id') as { [id: string]: Factor };
        let validFactorGroups = _.keyBy(Object.values(factorResources)
            .filter((f: FactorResource) => f.type === 'FACTOR_GROUP')
            .filter((fg: FactorResource) => Viewpoints.isGroupIncluded(fg as FactorGroup, factorResources, validFactorResources))
            , 'id')
        return _.merge(validFactorResources, validFactorGroups) as { [id: string]: FactorResource }
    }

    get unfilteredViewpoints() {
        let decisionsModule = getModule(Decisions);
        if (decisionsModule.selectedDecision && decisionsModule.decisionViewpoints) {
            return decisionsModule.decisionViewpoints[decisionsModule.selectedDecision.decisionId]
        }
    }

    get unfilteredViewpointsMap(): { [viewpointId: string]: Viewpoint } | undefined {
        if (this.unfilteredViewpoints) {
            return _.keyBy(this.unfilteredViewpoints, 'viewpointId')
        }

    }

    private static isGroupIncluded(factorGroup: FactorGroup, factorResources: { [id: string]: FactorResource }, includedFactors: { [id: string]: Factor }): boolean {
        return factorGroup.childrenIds
            .map((cId: string) => factorResources[cId])
            .filter((f: FactorResource) => {
                if (!f) return false;
                if (f.type === 'FACTOR') {
                    return includedFactors[f.id]
                } else {
                    return this.isGroupIncluded(f as FactorGroup, factorResources, includedFactors)
                }
            }).length > 0
    }

    // private static getListFactors() {
    //     let factorList = workspaceOptions.activeFactorLists;
    //     console.log(factorList)
    //     let allLists = getModule(Decisions).decisionLists
    //     let factorIds: string[] = [];
    //     let factorLists = allLists as { [listId: string]: FactorList };
    //     if (factorLists && factorList.length) {
    //         for (let id of Object.values(factorList)) {
    //             if (factorLists[id]) {
    //                 factorIds = factorIds.concat(factorLists[id].factors.map((id: FactorListItem) => id.factorId as string))
    //             } else {
    //                 continue;
    //             }

    //         }

    //         factorIds = _.uniq(factorIds);
    //         console.log(factorIds)
    //         let path = []
    //         for(let factor of factorIds) {
    //             path = path.concat(Viewpoints.findBranch(factor, modelling.publishedModelFactorResources, modelling.publishedModelFactorResources['M']))

    //         }
    //         path = _.uniq(path)
    //         return _.pick(modelling.publishedModelFactorResources,path)
    //     }
    // }

    // private static findBranch(searchingId: string, factorResources: any, factorResource: FactorResource): string[] | undefined {
    //     if (factorResource.id === searchingId) return [searchingId];
    //     if (factorResource.type !== 'FACTOR_GROUP') return undefined;
    //     let factorGroup = factorResource as FactorGroup;
    //     for (let childId of factorGroup.childrenIds) {
    //         let childPath = Viewpoints.findBranch(searchingId, factorResources, factorResources[childId]);
    //         if (childPath) {
    //             return [factorResource.id, ...childPath];
    //         }
    //     }
    // }

    @Mutation
    setActiveViewpointId(payload: string | null) {
        this.activeViewpointId = payload;
    }

    @Mutation
    addSelectedViewpointId(viewpointId: string) {
        if (!this.selectedViewpointIds.includes(viewpointId)) {
            let viewpointIds = this.selectedViewpointIds;
            viewpointIds.splice(viewpointIds.length, 0, viewpointId);
            this.selectedViewpointIds = viewpointIds;
        }
    }

    @Mutation
    removeSelectedViewpointId(viewpointId: string) {
        let index = this.selectedViewpointIds.indexOf(viewpointId);
        if (index >= 0) {
            this.selectedViewpointIds.splice(index, 1);
        }
    }

    @Mutation
    setViewpointFactorResourcesSet(payload: { viewpointId: string, factorResources: { [factorResourceId: string]: ViewpointFactorResource } }) {
        if(!this.viewpointFactorResources[payload.viewpointId]) {
            Vue.set(this.viewpointFactorResources, payload.viewpointId, {})
        }
        Object.values(payload.factorResources).forEach(f => { console.log(this.viewpointFactorResources[payload.viewpointId], payload.factorResources, f.id); Vue.set(this.viewpointFactorResources[payload.viewpointId], f.id, f)});
    }

    @Mutation
    setViewpointFactorResources(payload: { viewpointId: string, factorResources: { [factorResourceId: string]: ViewpointFactorResource } }) {
        Vue.set(this.viewpointFactorResources, payload.viewpointId, payload.factorResources);
    }

    @Mutation
    setViewpointFactorResource(payload: ViewpointFactorResource) {
        Vue.set(this.viewpointFactorResources[payload.viewpointId], payload.id, payload);
    }

    @Mutation
    setViewpointChoiceFactorResources(payload: { viewpointId: string, viewpointChoiceFactors: { [choiceId: string]: { [factorId: string]: ViewpointChoiceFactor } } }) {
        Vue.set(this.viewpointChoiceFactorResource, payload.viewpointId, payload.viewpointChoiceFactors);
    }

    @Mutation
    setViewpointChoiceFactorResource(viewpointChoiceFactor: ViewpointChoiceFactor) {
        if (!this.viewpointChoiceFactorResource[viewpointChoiceFactor.viewpointId]) {
            Vue.set(this.viewpointChoiceFactorResource, viewpointChoiceFactor.viewpointId,
                {[viewpointChoiceFactor.choiceId]: {[viewpointChoiceFactor.factorId]: viewpointChoiceFactor}})
        } else if (!this.viewpointChoiceFactorResource[viewpointChoiceFactor.viewpointId][viewpointChoiceFactor.choiceId]) {
            Vue.set(this.viewpointChoiceFactorResource[viewpointChoiceFactor.viewpointId], viewpointChoiceFactor.choiceId,
                {[viewpointChoiceFactor.factorId]: viewpointChoiceFactor})
        } else {
            Vue.set(this.viewpointChoiceFactorResource[viewpointChoiceFactor.viewpointId][viewpointChoiceFactor.choiceId],
                viewpointChoiceFactor.factorId, viewpointChoiceFactor);
        }
    }

    @Mutation
    setViewpointDataChanged(viewpointId: string) {
        if (this.decisionViewpoints) {
            let viewpoint = this.decisionViewpoints.find(v => v.viewpointId = viewpointId)
            if (viewpoint) {
                Vue.set(viewpoint, 'changed', true)
            }
        }
    }

    @Mutation
    clear() {
        this.viewpointFactorResources = {};
        this.viewpointChoiceFactorResource = {};
        this.selectedViewpointIds = [];
        this.activeViewpointId = null;
    }

    @Mutation
    clearSelections() {
        this.selectedViewpointIds = []
        this.activeViewpointId = null
    }

    @Action
    async clearSelecteViewpoints() {
        this.clearSelections();
    }

    @Action
    async selectActiveViewpoint(viewpointId: string | null = null) {
        if (viewpointId && !this.selectedViewpointIds.includes(viewpointId)) {
            await this.selectViewpoint(viewpointId);
        }
        this.setActiveViewpointId(viewpointId);
    }

    @Action
    async selectViewpoint(viewpointId: string) {
        if (!viewpointId) return;
        this.addSelectedViewpointId(viewpointId);
        let modelDefinition = getModule(Modelling).publishedDecisionModelDefinition;
        if (!modelDefinition) return;
        let viewpointFactorResourcesPromise = VIEWPOINT_API.getViewpointFactorResources(viewpointId);
        let scoringFuture = scoring.getScoresForViewpoint({
            decisionId: modelDefinition.modelId,
            revision: modelDefinition.revision,
            viewpointId: viewpointId
        })

        let viewpointChoiceFactors = (await VIEWPOINT_API.getViewpointChoiceFactors(viewpointId))
            .reduce((result: any, item: ViewpointChoiceFactor) => {
                let choice = result[item.choiceId] = result[item.choiceId] || {};
                choice[item.factorId] = item
                return result;
            }, {});

        let viewpointFactorResources = await viewpointFactorResourcesPromise
        this.setViewpointChoiceFactorResources({
            viewpointId: viewpointId,
            viewpointChoiceFactors: viewpointChoiceFactors
        });
        this.setViewpointFactorResources({
            viewpointId: viewpointId,
            factorResources: _.keyBy(viewpointFactorResources, 'id')
        });
        // reset all column width after select a new viewpoint
        await workspaceOptions.resetChoiceColumnsWidth();
    }

    @Action
    async deselectViewpoint(viewpointId: string) {
        this.removeSelectedViewpointId(viewpointId);
    }

    @Action
    async putViewpointFactor(viewpointFactor: ViewpointFactorResource) {
        let resultedViewpointFactor = await VIEWPOINT_API.putViewpointFactorResource(viewpointFactor.viewpointId, viewpointFactor);
        this.setViewpointFactorResource(resultedViewpointFactor)
        let autoCalc = getModule(WorkspaceOptions).autoCalc;
        if(autoCalc) {
            await scoring.refreshScoring()
            await scoring.recalculateAllChoiceScores(viewpointFactor.viewpointId);

        }

        return resultedViewpointFactor;
    }

    @Action
    async putViewpointFactors(payload: { viewpointId: string, viewpointFactors: ViewpointFactorResource[] }) {
        let viewpointFactorResources = await VIEWPOINT_API.putViewpointFactorResources(payload.viewpointId, payload.viewpointFactors);
        let autoCalc = getModule(WorkspaceOptions).autoCalc;

        console.log(viewpointFactorResources)
        this.setViewpointFactorResourcesSet({
            viewpointId: payload.viewpointId,
            factorResources: _.keyBy(viewpointFactorResources, 'id')
        })
        if(autoCalc && payload.viewpointId in this.decisionViewpointsMap) {
            await scoring.refreshScoring()
            await scoring.recalculateAllChoiceScores(payload.viewpointId);
        }

        return viewpointFactorResources;
    }

    @Action
    async putViewpointChoiceFactorScore(payload: ChoiceFactorScoreChange) {
        let viewpointChoiceFactor: ViewpointChoiceFactor;
        let autoCalc = getModule(WorkspaceOptions).autoCalc;
        if (this.viewpointChoiceFactorResource[payload.viewpointId] && this.viewpointChoiceFactorResource[payload.viewpointId][payload.choiceId]
            && this.viewpointChoiceFactorResource[payload.viewpointId][payload.choiceId][payload.factorId]) {
            viewpointChoiceFactor = this.viewpointChoiceFactorResource[payload.viewpointId][payload.choiceId][payload.factorId];
        } else {
            viewpointChoiceFactor = {
                choiceId: payload.choiceId,
                factorId: payload.factorId,
                viewpointId: payload.viewpointId,
            }
        }
        viewpointChoiceFactor.score = payload.score;
        viewpointChoiceFactor.scores = payload.scores;
        let addedViewpointChoiceFactors = await VIEWPOINT_API.putViewpointChoiceFactors(payload.viewpointId, [viewpointChoiceFactor]);
        addedViewpointChoiceFactors.forEach(v => this.setViewpointChoiceFactorResource(v));
        if(autoCalc){
            await scoring.refreshScoring()
            scoring.recalculateAllChoiceScores(payload.viewpointId);
        }
        getModule(Viewpoints).setViewpointDataChanged(payload.viewpointId);
    }

    @Action 
    async createViewpointChoiceFactors(payload: { viewpointId: string, choiceFactors: ViewpointChoiceFactor[] }) {
        let viewpointChoiceFactors = await VIEWPOINT_API.putViewpointChoiceFactors(payload.viewpointId, payload.choiceFactors);
        console.log(viewpointChoiceFactors)
        viewpointChoiceFactors.forEach(v => this.setViewpointChoiceFactorResource(v));
        return viewpointChoiceFactors;
    }


    @Action
    async putViewpointChoiceFactorScores(payload: ChoiceFactorsScoreChanges) {
        let autoCalc = getModule(WorkspaceOptions).autoCalc;
        let viewpointChoiceFactors = payload.scores.map(s => {
            let viewpointChoiceFactor: ViewpointChoiceFactor;
            if (this.viewpointChoiceFactorResource[payload.viewpointId] && this.viewpointChoiceFactorResource[payload.viewpointId][payload.choiceId]
                && this.viewpointChoiceFactorResource[payload.viewpointId][payload.choiceId][s.factorId]) {
                viewpointChoiceFactor = this.viewpointChoiceFactorResource[payload.viewpointId][payload.choiceId][s.factorId];
            } else {
                viewpointChoiceFactor = {
                    choiceId: payload.choiceId,
                    factorId: s.factorId,
                    viewpointId: payload.viewpointId,
                }
            }
            viewpointChoiceFactor.score = s.score;
            viewpointChoiceFactor.scores = s.scores;
            return viewpointChoiceFactor;
        });

        let addedViewpointChoiceFactors = await VIEWPOINT_API.putViewpointChoiceFactors(payload.viewpointId, viewpointChoiceFactors);
        addedViewpointChoiceFactors.forEach(v => this.setViewpointChoiceFactorResource(v));
        if (autoCalc) {
            await scoring.recalculateAllChoiceScores(payload.viewpointId);
        }
        getModule(Viewpoints).setViewpointDataChanged(payload.viewpointId);
    }

    @Action
    async updateViewpointFactorResourceScoringDefinition(payload: { viewpointId: string, factorResourceId: string, scoringDefinition: any | undefined }) {
        let viewpointFactorResource = this.viewpointFactorResources[payload.viewpointId][payload.factorResourceId];
        if (!viewpointFactorResource) {
            viewpointFactorResource = {
                viewpointId: payload.viewpointId,
                id: payload.factorResourceId,
                scoringDefinition: payload.scoringDefinition
            }
        }
        viewpointFactorResource.scoringDefinition = payload.scoringDefinition;
        return await this.putViewpointFactor(viewpointFactorResource);
    }

    @Action
    async cloneViewpoint(payload: {original: any, newViewpoint: any, includeWeights: boolean, includeKeys: boolean, includeScores: boolean}) {
        let decisionsModule = getModule(Decisions)
        let original = payload.original;
        let newViewpointParams = payload.newViewpoint
        //Create new viewpoint so that we have Id to work with
        let newViewpoint = await decisionsModule.createDecisionViewpoint({
            decisionId: decisionsModule.selectedDecision.decisionId,
            name: `${newViewpointParams.name}`,
            description: newViewpointParams.description
        })
        //Select original viewpoint to fetch viewpointfactor and choice resources, then remove it
        let oldViewpointFactorResources: ViewpointFactorResource[] = await VIEWPOINT_API.getViewpointFactorResources(original.viewpointId); 
        if (newViewpointParams.addToList) {
            await decisionsModule.addViewpointToActiveLists(newViewpoint.viewpointId)
        }
        //If old viewpoint has Vp factor resources
        if (oldViewpointFactorResources.length > 0) {
            let newViewpointFactorResources = oldViewpointFactorResources;
            let listOfViewpointFactors: ViewpointFactorResource[] = []
            //Loop through the cloned vpFactorResources and changed the vpId of the cloned resources to the new vpId
            for (let viewpointFactor of Object.values(newViewpointFactorResources)) {
                Vue.set(viewpointFactor, 'viewpointId', newViewpoint.viewpointId)
                if(!payload.includeWeights) {
                    Vue.delete(viewpointFactor, 'numWeightingDots')
                }

                if(!payload.includeKeys) {
                    Vue.delete(viewpointFactor, 'scoringDefinition')
                    Vue.delete(viewpointFactor, 'scoringKeyDefinition')
                }
                listOfViewpointFactors.push(viewpointFactor)
            }

            //If we have viewpointFactors in out list then put them into vuex and the backend
            if (listOfViewpointFactors.length > 0) {
                let res = await this.putViewpointFactors({ viewpointId: newViewpoint.viewpointId, viewpointFactors: listOfViewpointFactors });
            }

            let oldViewpointChoiceFactorResources: ViewpointChoiceFactor[] = await VIEWPOINT_API.getViewpointChoiceFactors(original.viewpointId); 

            let listOfViewpointChoiceFactors: ViewpointChoiceFactor[] = []
            //Check for cloned viewpointChoiceResources
            if (oldViewpointChoiceFactorResources && payload.includeScores) {
                for (let viewpointChoiceFactor of oldViewpointChoiceFactorResources) {
                    Vue.set(viewpointChoiceFactor, 'viewpointId', newViewpoint.viewpointId)
                    listOfViewpointChoiceFactors.push(viewpointChoiceFactor)
                }
                
                //If we have viewpoinChoiceFactors then put in vuex and backend
                if (listOfViewpointChoiceFactors.length > 0) {
                    let res = await this.createViewpointChoiceFactors({ viewpointId: newViewpoint.viewpointId, choiceFactors: listOfViewpointChoiceFactors })
                }
            }

            this.selectViewpoint(newViewpoint.viewpointId)
            return newViewpoint
            // NotificationEventBus.emitSuccessMessage();
        }
    }

    @Action
    async generateViewpoint(payload: any) {
        let sampleViewpoint = _.cloneDeep(payload);
        let decisionsModule = getModule(Decisions);
        let selectedDecision = decisionsModule.selectedDecision
        console.log(payload);
        if (!selectedDecision) return;
        let newViewpoint = await decisionsModule.createDecisionViewpoint({
            decisionId: selectedDecision.decisionId,
            name: sampleViewpoint.viewpointVariation != '' ? `${sampleViewpoint.name} - ${sampleViewpoint.viewpointVariation}` : sampleViewpoint.name,
            description: sampleViewpoint.description
        })

        let results = await VIEWPOINT_API.generateViewpoint(
            decisionsModule.selectedDecisionId,
            typeof sampleViewpoint.viewpoints === 'string' ? [sampleViewpoint.viewpoints]: sampleViewpoint.viewpoints,
            sampleViewpoint.viewpointVariation != ''
                ? sampleViewpoint.viewpointVariation.toLowerCase()
                : '',
            sampleViewpoint.generationFunction != ''
                ? sampleViewpoint.generationFunction
                : 'score_estimation_from_ranking'
        );
        
        let viewpointChoiceFactors: ViewpointChoiceFactor[] = [];
        for (let choice of Object.keys(results?.decisions[decisionsModule.selectedDecisionId].choices)) {
            for (let factor of Object.keys(results?.decisions[decisionsModule.selectedDecisionId].choices[choice].factors)) {
                let choiceFactor: ViewpointChoiceFactor = {
                    viewpointId: `${newViewpoint.viewpointId}`,
                    factorId: `${factor}`,
                    choiceId: `${choice}`,
                    score: results.decisions[decisionsModule.selectedDecisionId].choices[choice].factors[factor].score
                }
                viewpointChoiceFactors.push(choiceFactor)
            }
        }
        let viewpointFactors: ViewpointFactorResource[] = [];
        for (let factor of Object.keys(results?.decisions[decisionsModule.selectedDecisionId].factors)) {
            let vpFactor: ViewpointFactorResource = {
                viewpointId: `${newViewpoint.viewpointId}`,
                id: `${factor}`,
                numWeightingDots: Math.ceil(results?.decisions[decisionsModule.selectedDecisionId].factors[factor].weight)
            }
            viewpointFactors.push(vpFactor)
        }
        if (viewpointChoiceFactors) {
            await VIEWPOINT_API.putViewpointChoiceFactors(newViewpoint.viewpointId, viewpointChoiceFactors)
        }

        if (viewpointFactors) {
            await VIEWPOINT_API.putViewpointFactorResources(newViewpoint.viewpointId, viewpointFactors)
        }
        if (sampleViewpoint.addToList) {
            decisionsModule.addViewpointToActiveLists(newViewpoint.viewpointId)

        }
    }


}

interface ChoiceFactorScoreChange {
    viewpointId: string,
    factorId: string,
    choiceId: string,
    score?: number
    scores?: number[]
}


interface ChoiceFactorsScoreChanges {
    viewpointId: string,
    choiceId: string,
    scores: {
        factorId: string,
        score?: number
        scores?: number[]
    }[]
}

export {
    ChoiceFactorScoreChange,
    ChoiceFactorsScoreChanges
}